import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText,
  Divider, makeStyles, Typography, Box
} from '@material-ui/core';
import DashboardIcon from '@material-ui/icons/Dashboard';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import FavoriteIcon from '@material-ui/icons/Favorite';
import CategoryIcon from '@material-ui/icons/Category';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import NotificationsIcon from '@material-ui/icons/Notifications';
import PeopleIcon from '@material-ui/icons/People';
import BuildIcon from '@material-ui/icons/Build';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import FeedbackIcon from '@material-ui/icons/Feedback';
import ChatIcon from '@material-ui/icons/Chat';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import CollectionsIcon from '@material-ui/icons/Collections';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0 },
  drawerPaper: {
    width: drawerWidth,
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(180deg, #0f172a 0%, #1a1f35 100%)'
      : 'linear-gradient(180deg, #f5f0eb 0%, #efeae3 100%)',
    borderRight: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.08)'
      : '1px solid rgba(45,42,36,0.06)',
    boxShadow: theme.palette.type === 'dark'
      ? '4px 0 24px rgba(0,0,0,0.4)'
      : '4px 0 24px rgba(45,42,36,0.04)',
    overflowY: 'auto',
    overflowX: 'hidden' },
  logoSection: {
    padding: '28px 24px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 12 },
  logoIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default },
  logoText: {
    fontWeight: 800,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    letterSpacing: '-0.01em',
    lineHeight: 1.2 },
  logoSub: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' },
  sectionLabel: {
    padding: '16px 24px 8px',
    fontSize: '0.65rem',
    fontWeight: 700,
    color: theme.palette.text.disabled,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' },
  navList: {
    padding: '0 12px',
    flex: 1 },
  navItem: {
    borderRadius: 12,
    marginBottom: 2,
    padding: '10px 12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(16,185,129,0.08)',
      '& $navIcon': { color: '#10b981' },
      '& $navText span': { color: theme.palette.text.primary } } },
  navItemActive: {
    borderRadius: 12,
    marginBottom: 2,
    padding: '10px 12px',
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(200,200,200,0.1) 100%)'
      : 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(255,255,255,0.2)'
      : '1px solid rgba(16,185,129,0.2)',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(200,200,200,0.15) 100%)'
        : 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.15) 100%)',
    },
  },
  navIcon: {
    color: theme.palette.text.disabled,
    minWidth: 36,
    transition: 'color 0.2s ease' },
  navIconActive: {
    color: '#10b981',
    minWidth: 36,
    filter: 'drop-shadow(0 0 6px rgba(16,185,129,0.5))' },
  navText: {
    '& span': {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette.text.secondary,
      transition: 'color 0.2s ease' } },
  navTextActive: {
    '& span': {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#10b981' } },
  activeDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#10b981',
    boxShadow: '0 0 8px rgba(16,185,129,0.8)',
    marginLeft: 'auto',
    flexShrink: 0 },
  divider: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(148,163,184,0.08)'
      : 'rgba(45,42,36,0.06)',
    margin: '12px 16px' },
  bottomSection: {
    padding: '0 12px 20px' },
  logoutItem: {
    borderRadius: 12,
    padding: '10px 12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(239,68,68,0.08)',
      '& $logoutIcon': { color: '#ef4444' },
      '& $logoutText span': { color: '#ef4444' } } },
  logoutIcon: {
    color: theme.palette.text.disabled,
    minWidth: 36,
    transition: 'color 0.2s ease' },
  logoutText: {
    '& span': {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette.text.secondary,
      transition: 'color 0.2s ease' } },
  userChip: {
    margin: '0 12px 12px',
    padding: '10px 14px',
    borderRadius: 12,
    background: theme.palette.type === 'dark'
      ? 'rgba(148,163,184,0.06)'
      : 'rgba(45,42,36,0.04)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.1)'
      : '1px solid rgba(45,42,36,0.07)' },
  userName: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3 },
  userRole: {
    fontSize: '0.68rem',
    fontWeight: 500,
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: '0.08em' },
  versionTag: {
    margin: '12px 24px 4px',
    padding: '6px 10px',
    background: 'rgba(16,185,129,0.06)',
    borderRadius: 8,
    border: '1px solid rgba(16,185,129,0.12)' },
  versionText: {
    fontSize: '0.7rem',
    color: theme.palette.text.disabled,
    fontWeight: 500 } }));

const navItems = [
  { path: '/dashboard',     label: 'Dashboard',        permissionKey: 'dashboard',     icon: <DashboardIcon fontSize="small" /> },
  { path: '/app-version',   label: 'App Version',      permissionKey: 'maintenance',   icon: <SystemUpdateAltIcon fontSize="small" /> },
  { path: '/notifications', label: 'Notifications',    permissionKey: 'notifications', icon: <NotificationsIcon fontSize="small" /> },
  { path: '/campaigns',     label: 'In-App Messages',  permissionKey: 'banners',       icon: <AnnouncementIcon fontSize="small" /> },
  { path: '/hadith',        label: 'Hadith',           permissionKey: 'hadith',        icon: <MenuBookIcon fontSize="small" /> },
  { path: '/quran',         label: 'Quran',            permissionKey: 'quran',         icon: <MenuBookIcon fontSize="small" /> },
  { path: '/prophets',      label: 'Prophets',         permissionKey: 'prophets',      icon: <MenuBookIcon fontSize="small" /> },
  { path: '/duas',          label: 'Duas',             permissionKey: 'duas',          icon: <FavoriteIcon fontSize="small" /> },
  { path: '/tasbeeh',       label: 'Tasbeeh',          permissionKey: 'tasbeeh',       icon: <FavoriteIcon fontSize="small" /> },
  { path: '/categories',    label: 'Categories',       permissionKey: 'categories',    icon: <CategoryIcon fontSize="small" /> },
  { path: '/banners',       label: 'Banners',          permissionKey: 'banners',       icon: <AnnouncementIcon fontSize="small" /> },
  { path: '/gallery',       label: 'Image Gallery',    permissionKey: 'banners',       icon: <CollectionsIcon fontSize="small" /> },
  { path: '/quotes',        label: 'Quotes',           permissionKey: 'quotes',        icon: <FormatQuoteIcon fontSize="small" /> },
  { path: '/users',         label: 'App Users',        permissionKey: 'users',         icon: <PeopleIcon fontSize="small" /> },
  { path: '/feedback',      label: 'Feedback',         permissionKey: 'feedback',      icon: <FeedbackIcon fontSize="small" /> },
  { path: '/messages',      label: 'Messages',         permissionKey: 'messages',      icon: <ChatIcon fontSize="small" /> },
  { path: '/tickets',       label: 'Tickets',          permissionKey: 'tickets',       icon: <FeedbackIcon fontSize="small" /> },
  { path: '/maintenance',   label: 'Maintenance',      permissionKey: 'maintenance',   icon: <BuildIcon fontSize="small" /> },
  { path: '/admin-users',   label: 'Admin Users',      permissionKey: 'admin_users',   icon: <SupervisorAccountIcon fontSize="small" /> },
];

export default function Sidebar() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { logout, user, hasPermission } = useAuth();

  const handleLogout = () => {
    logout();
    history.push('/login');
  };

  const visibleItems = navItems.filter((item) => hasPermission(item.permissionKey));

  return (
    <Drawer
      className={classes.drawer}
      variant="permanent"
      classes={{ paper: classes.drawerPaper }}
      anchor="left"
    >
      <div className={classes.logoSection}>
        <div className={classes.logoIcon}>
          <img src="/logo.png" alt="Noor Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <div>
          <Typography className={classes.logoText}>Noor</Typography>
          <Typography className={classes.logoSub}>Admin Panel</Typography>
        </div>
      </div>

      <Divider className={classes.divider} />

      {/* Logged-in user chip */}
      {user && (
        <div className={classes.userChip}>
          <Typography className={classes.userName}>{user.name || user.email}</Typography>
          <Typography className={classes.userRole}>{(user.role || 'admin').replace('_', ' ')}</Typography>
        </div>
      )}

      <Typography className={classes.sectionLabel}>Navigation</Typography>

      <List className={classes.navList} disablePadding>
        {visibleItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <ListItem
              button
              key={item.path}
              className={isActive ? classes.navItemActive : classes.navItem}
              onClick={() => history.push(item.path)}
              disableRipple
            >
              <ListItemIcon className={isActive ? classes.navIconActive : classes.navIcon}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                className={isActive ? classes.navTextActive : classes.navText}
              />
              {isActive && <div className={classes.activeDot} />}
            </ListItem>
          );
        })}
      </List>

      <Box flexGrow={1} />

      <Divider className={classes.divider} />

      <div className={classes.bottomSection}>
        <ListItem button className={classes.logoutItem} onClick={handleLogout} disableRipple>
          <ListItemIcon className={classes.logoutIcon}>
            <ExitToAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign Out" className={classes.logoutText} />
        </ListItem>

        <div className={classes.versionTag}>
          <Typography className={classes.versionText}>Noor Admin v2.0 · Firebase</Typography>
        </div>
      </div>
    </Drawer>
  );
}
